import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { FormattedMessage } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./notifications.css";
import { Bell, Sparkles, TriangleAlert } from "lucide-react";
import alertaumbral from "../../assets/alertaumbral.png";
import alertatunel from "../../assets/alertatunel.png";
import alertaausencia from "../../assets/alertafaltadatos.png";
import alertamaximetro from "../../assets/alertamaximetro.png";
import alertareactiva from "../../assets/alertareactiva.png";
import alertacoste from "../../assets/alertacoste.png";
import {
  getNotifications,
  updateNotificationReadStatusUnified,
} from "../../redux/apiCalls/notifications/notificationsApiCalls";

const parseNotificationDate = (dateStr) => {
  if (!dateStr) return 0;

  const value = String(dateStr).trim();

  if (value.includes("/")) {
    const [datePart, timePart = "00:00:00"] = value.split(", ");
    const [day, month, year] = datePart.split("/").map(Number);
    const [hours = 0, minutes = 0, seconds = 0] = timePart
      .split(":")
      .map(Number);

    return new Date(year, month - 1, day, hours, minutes, seconds).getTime();
  }

  const parsed = new Date(value).getTime();
  if (!Number.isNaN(parsed)) return parsed;

  if (value.includes("-")) {
    const [datePart, timePart = "00:00:00"] = value.split(" ");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hours = 0, minutes = 0, seconds = 0] = timePart
      .split(":")
      .map(Number);

    return new Date(year, month - 1, day, hours, minutes, seconds).getTime();
  }

  return 0;
};

const formatDate = (date) => {
  if (!date) return "";

  const ts = parseNotificationDate(date);
  if (!ts) return String(date);

  const d = new Date(ts);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");

  return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
};

const normalizeAlertType = (value) => {
  return String(value || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

const getAlertEventRedirectRoute = (notificationItem) => {
  const rawType =
    notificationItem?.type ??
    notificationItem?.alert?.type ??
    notificationItem?.raw?.type ??
    "";

  const normalizedType = normalizeAlertType(rawType);

  const routesByType = {
    MAXIMETRO: "/alerts-events?type=MAXIMETRO&page=1",
    AUSENCIA: "/alerts-events?type=AUSENCIA&page=1",
    UMBRAL: "/alerts-events?type=UMBRAL&page=1",
    COSTE: "/alerts-events?type=COSTE&page=1",
    TUNEL: "/alerts-events?type=TUNEL&page=1",
    REACTIVA: "/alerts-events?type=REACTIVA&page=1",
  };

  return routesByType[normalizedType] || "/alerts-events?page=1";
};

export const extractNormalizedNotification = (item) => {
  if (!item || typeof item !== "object") {
    return null;
  }

  const source = item?.source || "report";
  if (source === "communication") {
    return null;
  }

  const healthSafetyReport = item?.healthSafetyReport ?? null;
  const sourceId = Number(item?.source_id ?? item?.notification_id);
  const fallbackId = Number.isFinite(sourceId) ? sourceId : null;
  const recommendationId = Number(
    item?.recommendation_id ?? item?.related_entity_id,
  );
  const normalizedRecommendationId = Number.isFinite(recommendationId)
    ? recommendationId
    : null;

  return {
    id: `${source}-${fallbackId ?? item?.source_id}`,
    uuid: item?.uuid ?? (fallbackId !== null ? String(fallbackId) : null),
    originalId: fallbackId ?? item?.source_id,
    source,
    isRead: !!item?.is_read,
    title: item?.title ?? "",
    text: item?.text ?? "",
    type: item?.type ?? "",
    priority: item?.priority ?? null,
    dateToShow: item?.date_to_show ?? item?.created_at ?? null,
    communicationPostId: item?.communication_post_id ?? null,
    recommendationId: normalizedRecommendationId,
    healthSafetyReport,
    deviceName: item?.device?.name ?? "",
    redirectTo: (() => {
      if (source === "event") {
        return getAlertEventRedirectRoute(item);
      }

      if (source === "recommendation") {
        return null;
      }

      if (source === "automatic_report") {
        return item?.redirect_to || item?.payload?.navigation_path || "/reports";
      }

      return "/notifications";
    })(),
    raw: item,
  };
};

export const isVisibleByFrontRules = (item, locallyReadIds) => {
  const norm = item?.raw ? extractNormalizedNotification(item.raw) : item;

  if (!norm) return false;
  if (locallyReadIds?.has?.(norm.id)) return false;
  if (norm.isRead) return false;

  return true;
};

const getAlertEventImage = (eventType) => {
  switch (normalizeAlertType(eventType)) {
    case "UMBRAL":
      return alertaumbral;
    case "AUSENCIA":
      return alertaausencia;
    case "MAXIMETRO":
      return alertamaximetro;
    case "REACTIVA":
      return alertareactiva;
    case "COSTE":
      return alertacoste;
    case "TUNEL":
      return alertatunel;
    default:
      return null;
  }
};

const getNotificationIcon = (item) => {
  if (item.source === "event") {
    const imageSrc = getAlertEventImage(item.type);

    if (imageSrc) {
      return (
        <img
          src={imageSrc}
          alt={item.type || "Alerta"}
          className="notification-alert-image"
        />
      );
    }

    return (
      <Bell
        size={18}
        className="notification-icon notification-icon--default"
      />
    );
  }

  if (item.source === "recommendation") {
    return (
      <Sparkles
        size={18}
        className="notification-icon notification-icon--default"
      />
    );
  }

  switch (String(item.type).toLowerCase()) {
    case "report_expiration":
      return (
        <TriangleAlert
          size={18}
          className="notification-icon notification-icon--warning"
        />
      );
    case "ai_recommendation_needs_review":
    case "ai_recommendation_published":
    case "recommendation_published":
      return (
        <Sparkles
          size={18}
          className="notification-icon notification-icon--default"
        />
      );
    default:
      return (
        <Bell
          size={18}
          className="notification-icon notification-icon--default"
        />
      );
  }
};

const NotificationModal = ({
  isOpen,
  setIsOpen,
  onNotificationUpdate,
  anchorRef,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const devices = useSelector((state) => state.devices?.currentDevices);
  const notificationsState = useSelector((state) => state.notifications);

  const dropdownRef = useRef(null);
  const [locallyReadIds, setLocallyReadIds] = useState(() => new Set());
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const assignedDeviceIds = useMemo(
    () =>
      Array.from(
        new Set(
          (devices || [])
            .map((d) => Number(d?.id))
            .filter((n) => Number.isFinite(n))
        )
      ),
    [devices]
  );

  const loadNotifications = useCallback(async () => {
    if (!user?.currentUser?.accessToken) return;

    try {
      await getNotifications(dispatch, user.currentUser.accessToken, {
        include: ["count", "items"],
        // Keep AI recommendations out of the global notification center for now.
        sources: assignedDeviceIds.length > 0
          ? ["event", "report", "automatic_report"]
          : ["report", "automatic_report"],
        page: 1,
        limit: 100,
        device_ids: assignedDeviceIds,
      });
    } catch (error) {
      console.error("Error loading notifications:", error);
    }
  }, [dispatch, user?.currentUser?.accessToken, assignedDeviceIds]);

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, loadNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpen) return;

      const clickedInsideDropdown = dropdownRef.current?.contains(event.target);
      const clickedInsideAnchor = anchorRef?.current?.contains(event.target);

      if (!clickedInsideDropdown && !clickedInsideAnchor) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, setIsOpen, anchorRef]);

  const visibleItems = useMemo(() => {
    return (notificationsState?.items || [])
      .map(extractNormalizedNotification)
      .filter(Boolean)
      // AI recommendations stay out of the global bell while this temporary switch is active.
      .filter((item) => item.source !== "recommendation")
      .filter((item) => isVisibleByFrontRules(item, locallyReadIds))
      .sort((a, b) => {
        return parseNotificationDate(b.dateToShow) - parseNotificationDate(a.dateToShow);
      });
  }, [notificationsState?.items, locallyReadIds]);

  useEffect(() => {
    if (typeof onNotificationUpdate === "function") {
      const healthSafetyUnread = visibleItems.filter(
        (item) => item.source === "report"
      ).length;

      const automaticReportsUnread = visibleItems.filter(
        (item) => item.source === "automatic_report"
      ).length;

      const alertEventsUnread = visibleItems.filter(
        (item) => item.source === "event"
      ).length;

      const communicationUnread = visibleItems.filter(
        (item) => item.source === "communication"
      ).length;

      const recommendationUnread = visibleItems.filter(
        (item) => item.source === "recommendation"
      ).length;

      onNotificationUpdate({
        total: visibleItems.length,
        healthSafetyUnread: healthSafetyUnread + automaticReportsUnread,
        automaticReportsUnread,
        alertEventsUnread,
        communicationUnread,
        recommendationUnread,
      });
    }
  }, [visibleItems, onNotificationUpdate]);

  const handleMarkAsRead = async (item) => {
    try {
      await updateNotificationReadStatusUnified(
        dispatch,
        {
          source: item.source,
          id: item.originalId,
          is_read: true,
        },
        user.currentUser.accessToken
      );

      setLocallyReadIds((prev) => {
        const next = new Set(prev);
        next.add(item.id);
        return next;
      });
    } catch (error) {
      console.error("Error updating item:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!visibleItems.length) return;

    setIsMarkingAll(true);

    try {
      await Promise.all(
        visibleItems.map((item) =>
          updateNotificationReadStatusUnified(
            dispatch,
            {
              source: item.source,
              id: item.originalId,
              is_read: true,
            },
            user.currentUser.accessToken
          )
        )
      );

      setLocallyReadIds((prev) => {
        const next = new Set(prev);
        visibleItems.forEach((item) => next.add(item.id));
        return next;
      });
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    } finally {
      setIsMarkingAll(false);
    }
  };

  const handleNotificationClick = async (item) => {
    try {
      if (!item.isRead) {
        await handleMarkAsRead(item);
      }

      setIsOpen(false);

      if (item.redirectTo) {
        navigate(item.redirectTo);
      }
    } catch (error) {
      console.error("Error handling notification click:", error);
    }
  };

  const isLoading = notificationsState?.isFetching;

  if (!isOpen) return null;

  return (
    <div ref={dropdownRef} className="notification-dropdown">
      <div className="notification-dropdown-header">
        <div className="notification-dropdown-title">
          <Bell size={18} className="me-2" />
          <FormattedMessage
            id="NotificationModal.Title"
            defaultMessage="Notificaciones"
          />
        </div>

        {visibleItems.length > 0 && (
          <button
            type="button"
            className="notification-mark-all-btn"
            onClick={handleMarkAllAsRead}
            disabled={isMarkingAll}
          >
            {isMarkingAll ? "Marcando..." : "Marcar todas como leídas"}
          </button>
        )}
      </div>

      <div className="notification-dropdown-body">
        {isLoading ? (
          <div className="notification-loading">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="notification-empty">
            <Bell size={30} className="mb-2" />
            <p>
              <FormattedMessage
                id="NotificationModal.NoNotifications"
                defaultMessage="No tienes notificaciones pendientes"
              />
            </p>
          </div>
        ) : (
          <div className="notification-list">
            {visibleItems.map((item) => (
              <div
                key={item.id}
                onClick={() => handleNotificationClick(item)}
                className={`notification-item-dropdown ${!item.isRead ? "unread" : ""}`}
              >
                <div className="notification-item-dropdown-icon">
                  {getNotificationIcon(item)}
                </div>

                <div className="notification-item-dropdown-content">
                  <h6 className="mb-1">
                    {item.title}
                    {item.dateToShow ? ` - ${formatDate(item.dateToShow)}` : ""}
                  </h6>

                  <p className="mb-1">{item.text}</p>

                  {item.source === "report" && item.healthSafetyReport && (
                    <div className="report-info">
                      <small className="text-muted">
                        <strong>Informe:</strong> {item.healthSafetyReport.title}
                      </small>
                      <br />
                      <small className="text-muted">
                        <strong>Categoría:</strong> {item.healthSafetyReport.category}
                      </small>
                      <br />
                      <small className="text-muted">
                        <strong>Vencimiento:</strong>{" "}
                        {formatDate(item.healthSafetyReport.expiration_date)}
                      </small>
                    </div>
                  )}

                  {item.source === "event" && item.deviceName && (
                    <div className="report-info">
                      <small className="text-muted">
                        <strong>Dispositivo:</strong> {item.deviceName}
                      </small>
                      {item.type && (
                        <>
                          <br />
                          <small className="text-muted">
                            <strong>Tipo:</strong> {item.type}
                          </small>
                        </>
                      )}
                    </div>
                  )}

                  {item.source === "recommendation" && (
                    <div className="report-info">
                      <small className="text-muted">
                        <strong>Recomendación:</strong>{" "}
                        {item.recommendationId ?? item.originalId}
                      </small>
                      <br />
                      <small className="text-muted">
                        <strong>Prioridad:</strong> {item.priority || "medium"}
                      </small>
                      {item.type && (
                        <>
                          <br />
                          <small className="text-muted">
                            <strong>Evento:</strong> {item.type}
                          </small>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {!item.isRead && <span className="notification-unread-dot" />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationModal;
